import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, Clock, Pause, Play, CheckCircle2, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";

const fmtUtc = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AutoSendStatus() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diagTo, setDiagTo] = useState("");
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const load = async () => {
    try { const r = await api.get("/admin/newsletter/auto-status"); setS(r.data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const togglePause = async () => {
    if (!s) return;
    setBusy(true);
    try {
      await api.post("/admin/newsletter/auto-pause", { paused: s.enabled });
      toast.success(s.enabled ? "Auto-send paused" : "Auto-send resumed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  const runDiag = async () => {
    const to = (diagTo || "").trim();
    if (!to) { toast.error("Enter an email address to diagnose"); return; }
    setDiagBusy(true); setDiagResult(null);
    try {
      const r = await api.post("/admin/newsletter/diag-send", { to });
      setDiagResult(r.data);
      if (r.data.ok) toast.success(`Sent to ${to} — check your inbox`);
      else toast.error(`Send failed: ${r.data.error || "unknown"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diag request failed");
    } finally { setDiagBusy(false); }
  };

  if (!s) return null;
  const last = s.last_run || {};
  const lastWasSkip = !last.sent_at && last.skipped_at;
  const lastWasError = !!last.error_at;

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="auto-send-status">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky" />
            <div className="font-display font-bold text-ink text-lg tracking-tight">Auto Monday Briefing</div>
            <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded ${s.enabled ? "bg-emerald-100 text-emerald-900" : "bg-stone-200 text-stone-700"}`}>
              {s.enabled ? "Active" : "Paused"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-sends to all <span className="font-semibold text-ink">{s.active_subscribers}</span> active subscribers every Monday 07:00 UK.
            Skips automatically if fewer than <span className="font-semibold">{s.min_new_vacancies_to_send}</span> new vacancies have been added since the last send.
          </p>
          <div className="mt-3 text-sm">
            Next send: <span className="font-mono text-ink">{fmtUtc(s.next_run_utc)}</span> UK
          </div>
        </div>
        <Button
          onClick={togglePause}
          disabled={busy}
          variant={s.enabled ? "outline" : "default"}
          className={s.enabled ? "" : "bg-amber text-ink hover:bg-amber-600 font-semibold"}
          data-testid="auto-send-toggle"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : s.enabled ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
          {s.enabled ? "Pause auto-send" : "Resume auto-send"}
        </Button>
      </div>

      {(last.sent_at || lastWasSkip || lastWasError) && (
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          {last.sent_at && (
            <>
              <Stat label="Last sent" value={fmtUtc(last.sent_at)} />
              <Stat label="Delivered" value={`${last.sent ?? 0}/${last.recipients ?? 0}`} ok={last.failed === 0} warn={last.failed > 0} />
              <Stat label="New vacancies" value={last.new_count ?? "—"} />
              <Stat label="Subject" value={last.subject || "—"} mono small />
            </>
          )}
          {lastWasSkip && !last.sent_at && (
            <>
              <Stat label="Last skipped" value={fmtUtc(last.skipped_at)} warn />
              <Stat label="Reason" value={(last.skip_reason || "").replaceAll("_", " ")} />
              <Stat label="New vacancies" value={last.new_count ?? "—"} />
            </>
          )}
          {lastWasError && (
            <Stat label="Last error" value={last.error || "—"} warn small />
          )}
        </div>
      )}

      {/* Surface the per-send error from the most recent auto-run when delivery failed */}
      {last.failed > 0 && last.first_error && (
        <div className="mt-4 bg-rose-50 border border-rose-200 rounded-md p-3 text-xs text-rose-900" data-testid="auto-send-error-detail">
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Resend reported {last.failed} failure{last.failed === 1 ? "" : "s"} during the last auto-run
          </div>
          <div className="font-mono break-all"><span className="text-rose-700">First error:</span> {last.first_error}</div>
          {Array.isArray(last.error_samples) && last.error_samples.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-rose-800">Show {last.error_samples.length} error sample{last.error_samples.length === 1 ? "" : "s"}</summary>
              <ul className="mt-1 space-y-1 font-mono">
                {last.error_samples.map((es, i) => (
                  <li key={i} className="break-all">→ {es.to}: <span className="text-rose-700">{es.error}</span></li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* One-shot diagnostic send — proves end-to-end Resend → inbox quickly */}
      <div className="mt-5 pt-5 border-t border-border" data-testid="auto-send-diag">
        <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mb-2">Diagnostic: send one test email now</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="your-email@example.com"
            value={diagTo}
            onChange={(e) => setDiagTo(e.target.value)}
            className="max-w-md"
            data-testid="auto-send-diag-to"
          />
          <Button onClick={runDiag} disabled={diagBusy} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="auto-send-diag-run">
            {diagBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send diagnostic
          </Button>
        </div>
        {diagResult && (
          <div className={`mt-3 rounded-md border p-3 text-xs ${diagResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
            <div className="font-semibold mb-1 flex items-center gap-1.5">
              {diagResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {diagResult.ok ? "Sent successfully" : "Send failed"}
            </div>
            <div className="font-mono break-all">
              <div>from: {diagResult.sender}</div>
              <div>to: {diagResult.recipient}</div>
              {diagResult.error && <div className="text-rose-700">error: {diagResult.error}</div>}
              <div>resend_configured: {String(diagResult.resend_configured)} · domain_verified: {String(diagResult.domain_verified)}</div>
              <div>site_url: {diagResult.site_url}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, ok, warn, mono, small }) {
  return (
    <div className="bg-paper border border-border rounded p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
        {ok && <CheckCircle2 className="w-3 h-3 text-emerald-700" />}
        {warn && <AlertTriangle className="w-3 h-3 text-amber-700" />}
        {label}
      </div>
      <div className={`mt-1 text-ink font-semibold ${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}
